import { tr } from "./i18n.js";
/**
 * Clipboard → image-file helpers.
 *
 * Why this module exists
 * ----------------------
 * Every image upload in Newsdesk (featured image, body images, custom
 * article images, ad logos and style assets) takes a `File` and posts
 * it as multipart. A clipboard image is a `Blob` with no filename, so
 * the only thing standing between "copy a screenshot" and "upload" is
 * a small amount of plumbing:
 *
 *   * pull the image out of a paste event (Cmd/Ctrl+V), OR
 *   * pull it out of `navigator.clipboard.read()` (button press —
 *     the path that works on iPad, where a keyboard paste into a
 *     non-focused region is unreliable),
 *   * give it a filename,
 *   * convert it when the target upload only accepts certain types
 *     (the ad endpoints are PNG/JPEG/WebP only; Safari sometimes
 *     hands over `image/tiff`).
 *
 * Everything here is framework-free and (except for the canvas
 * conversion and the real clipboard read) pure, so it is unit-tested
 * directly with node — see `frontend/scripts/clipboardImage.test.mjs`.
 *
 * The paste-target stack
 * ----------------------
 * Several surfaces want a window-level paste handler at once (the
 * article page listens for a paste to set the featured image; opening
 * the "add body image" modal should take that paste instead). Rather
 * than racing two `window.addEventListener("paste")` handlers and
 * hoping the propagation order works out, handlers register on a
 * stack and only the TOP one is called. Mount order does the rest:
 * the modal mounts after the page, so while it is open it wins, and
 * unmounting hands the paste back to the page.
 */

// Matches the 25 MB ceiling every image endpoint enforces server-side.
export const DEFAULT_MAX_BYTES = 25 * 1024 * 1024;

// Upload targets that only take these three (ads / style assets).
export const IMAGE_ACCEPT_ANY = "image/*";

const EXT_BY_TYPE = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/avif": "avif",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/tiff": "tiff",
  "image/bmp": "bmp",
  "image/svg+xml": "svg",
};

/** `"image/png,image/webp"` → `["image/png", "image/webp"]`. */
export function normalizeAccept(accept) {
  if (!accept) return [IMAGE_ACCEPT_ANY];
  const list = (Array.isArray(accept) ? accept : String(accept).split(","))
    .map((s) => String(s).trim().toLowerCase())
    .filter(Boolean);
  return list.length ? list : [IMAGE_ACCEPT_ANY];
}

/** Does `type` satisfy an `accept` list (supports the `image/*` wildcard)? */
export function isTypeAccepted(type, accept) {
  const t = String(type || "").toLowerCase();
  if (!t.startsWith("image/")) return false;
  return normalizeAccept(accept).some(
    (a) => a === IMAGE_ACCEPT_ANY || a === "*/*" || a === t,
  );
}

function pad(n) {
  return String(n).padStart(2, "0");
}

/**
 * A stable, sortable filename for a pasted blob:
 * `pasted-20260921-143012.png`. Clipboard blobs arrive nameless and
 * the backend derives nothing from the name, but a real name keeps
 * multipart happy and makes uploads legible in logs and in the
 * custom-article image list (which shows `file.name`).
 */
export function pastedFileName(type, now = new Date()) {
  const ext = EXT_BY_TYPE[String(type || "").toLowerCase()] || "png";
  return (
    `pasted-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.${ext}`
  );
}

/** Wrap a clipboard `Blob` as a named `File`. */
export function fileFromBlob(blob, now = new Date()) {
  const type = blob?.type || "image/png";
  return new File([blob], pastedFileName(type, now), { type });
}

/**
 * First image entry in a `DataTransferItemList` (or any array-like of
 * `{kind, type, getAsFile()}`). Returns the item, not the file, so
 * callers can decide whether to read it.
 */
export function pickImageItem(items, accept = IMAGE_ACCEPT_ANY) {
  const list = Array.from(items || []);
  return (
    list.find(
      (it) =>
        it &&
        it.kind === "file" &&
        isTypeAccepted(it.type, IMAGE_ACCEPT_ANY) &&
        (accept === IMAGE_ACCEPT_ANY || isTypeAccepted(it.type, accept)),
    ) ||
    // Fall back to ANY image even when it misses `accept` — the caller
    // converts it (see `coerceImageType`) rather than silently doing
    // nothing, which is what an editor experiences as "paste is broken".
    list.find(
      (it) => it && it.kind === "file" && isTypeAccepted(it.type, IMAGE_ACCEPT_ANY),
    ) ||
    null
  );
}

/** Extract an image `File` from a paste event, or null if it carries none. */
export function imageFileFromClipboardEvent(event, now = new Date()) {
  const data = event?.clipboardData;
  if (!data) return null;
  const item = pickImageItem(data.items);
  if (item) {
    const f = item.getAsFile?.();
    if (f) return f.name ? f : fileFromBlob(f, now);
  }
  // Safari occasionally exposes the image only through `files`.
  const file = Array.from(data.files || []).find((f) =>
    isTypeAccepted(f?.type, IMAGE_ACCEPT_ANY),
  );
  return file ? (file.name ? file : fileFromBlob(file, now)) : null;
}

/**
 * True when the paste landed in something the editor is typing into,
 * in which case we leave the event alone: a paste inside the body
 * editor, a headline field or a caption box belongs to that field.
 */
export function isEditableTarget(el) {
  let node = el;
  while (node) {
    const tag = String(node.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return true;
    if (node.isContentEditable === true) return true;
    if (
      typeof node.getAttribute === "function" &&
      node.getAttribute("contenteditable") === "true"
    ) {
      return true;
    }
    node = node.parentElement || node.parentNode || null;
    if (node && node.nodeType !== 1) return false;
  }
  return false;
}

/** Is the async clipboard-read API (the "Paste image" button) usable? */
export function clipboardReadSupported(nav = globalThis.navigator) {
  return !!(nav && nav.clipboard && typeof nav.clipboard.read === "function");
}

/**
 * Read one image off the system clipboard. Resolves to a named `File`.
 * Rejects with a message meant to be shown verbatim in a toast — the
 * raw DOMExceptions ("Read permission denied.") tell an editor
 * nothing about what to do next.
 */
export async function readImageFromClipboard(nav = globalThis.navigator) {
  if (!clipboardReadSupported(nav)) {
    throw new Error(
      tr("This browser can't read the clipboard directly — press Cmd/Ctrl+V instead."),
    );
  }
  let items;
  try {
    items = await nav.clipboard.read();
  } catch (e) {
    const name = e?.name || "";
    if (name === "NotAllowedError") {
      throw new Error(
        tr("Clipboard permission was denied. Allow clipboard access for this site, or press Cmd/Ctrl+V instead."),
      );
    }
    throw new Error(
      tr("Could not read the clipboard — press Cmd/Ctrl+V instead."),
    );
  }
  for (const item of items || []) {
    const type = (item.types || []).find((t) =>
      isTypeAccepted(t, IMAGE_ACCEPT_ANY),
    );
    if (!type) continue;
    const blob = await item.getType(type);
    return fileFromBlob(blob);
  }
  throw new Error(tr("No image on the clipboard — copy an image first."));
}

/**
 * Re-encode `file` to PNG when its type isn't in `accept`. Used for
 * the ad endpoints, which store by content-type and only expect
 * PNG/JPEG/WebP, and for Safari's occasional `image/tiff` paste.
 * Returns the original file untouched when it already qualifies.
 */
export async function coerceImageType(file, accept = IMAGE_ACCEPT_ANY) {
  if (isTypeAccepted(file?.type, accept)) return file;
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") {
    throw new Error(
      tr("Pasted image is {type} — save it as PNG and upload it instead.", { type: file?.type || tr("an unsupported type") }),
    );
  }
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(
      tr("Pasted image is {type} — save it as PNG and upload it instead.", { type: file?.type || tr("an unsupported type") }),
    );
  }
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0);
  bitmap.close?.();
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Canvas encoding failed"))),
      "image/png",
    );
  });
  return new File([blob], pastedFileName("image/png"), { type: "image/png" });
}

/**
 * Size guard mirroring the server's 25 MB ceiling, so an oversized
 * paste fails instantly with a readable message instead of after a
 * slow upload through Cloudflare.
 */
export function checkPastedSize(file, maxBytes = DEFAULT_MAX_BYTES) {
  if (file && file.size > maxBytes) {
    throw new Error(
      tr("Pasted image is {toFixed} MB — the limit is {round} MB.", { toFixed: (file.size / 1024 / 1024).toFixed(1), round: Math.round(maxBytes / 1024 / 1024) }),
    );
  }
  return file;
}

// ---- paste-target stack ---------------------------------------------------

const pasteStack = [];
let installedDoc = null;
let installedListener = null;

function dispatchPaste(event) {
  const top = pasteStack[pasteStack.length - 1];
  if (top) top(event);
}

/**
 * Register `handler` as THE paste handler until the returned function
 * is called. Handlers stack; only the most recently registered one
 * receives paste events, so a modal transparently takes over from the
 * page behind it and hands control back when it closes.
 */
export function pushPasteTarget(handler, doc = globalThis.document) {
  if (typeof handler !== "function") return () => {};
  pasteStack.push(handler);
  if (doc && installedDoc !== doc) {
    if (installedDoc && installedListener) {
      installedDoc.removeEventListener("paste", installedListener);
    }
    installedListener = dispatchPaste;
    installedDoc = doc;
    doc.addEventListener("paste", installedListener);
  }
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const i = pasteStack.lastIndexOf(handler);
    if (i >= 0) pasteStack.splice(i, 1);
    if (!pasteStack.length && installedDoc && installedListener) {
      installedDoc.removeEventListener("paste", installedListener);
      installedDoc = null;
      installedListener = null;
    }
  };
}

/** Test seam: how many handlers are currently registered. */
export function pasteTargetDepth() {
  return pasteStack.length;
}